import { Box } from '@coolblue-development/freddy';
import closeCollectionTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/closeCollectionTrigger';
import openCollectionTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/openCollectionTrigger';
import selectOptionTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/selectOptionTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { AvailabilityStates } from '@coolblue-development/next-availability-state';
import {
  CollectionDropdown,
  CollectionDropdownOption
} from '@coolblue-development/next-patterns/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import type { Product as GlobalProduct } from '@features/product/typings';
import { useIsLeasePage } from '@hooks/useIsLeasePage';
import { useTranslation } from '@i18n/useTranslation';
import {
  getCollectionProductInfo,
  getLanguageSpecificPrice,
  getPriceDifference
} from '@utils/collectionUtils';
import { useRouter } from 'next/navigation';
import { Fragment } from 'react';

import {
  hasHandoverSessionCookie,
  renewHandoverSessionCookie
} from '../../OrderBlock/BusinessLeasing/HandoverSession';
import { Discriminant, Product, Variant } from '../Collection.types';

type DropdownCollectionProps = {
  discriminant: Discriminant;
  shouldShowPriceDifference: boolean;
  variant?: Variant;
  onProductClick?: (product: Product | null) => void;
  productsWithCurrentColor: number[];
  product?: GlobalProduct;
};

export const DropdownCollection = ({
  discriminant,
  shouldShowPriceDifference,
  variant = Variant.DESKTOP,
  onProductClick,
  productsWithCurrentColor = [],
  product
}: DropdownCollectionProps) => {
  const { t } = useTranslation('webshop.productPage');
  const { trackEvent } = useAnalytics();
  const { language, isInternalUser } = useRequestContext();
  const router = useRouter();
  const isLeasePage = useIsLeasePage();

  const currentValue = discriminant.values.find(value => value.isValueOfCurrentProduct);
  const currentProductName = currentValue?.name || '';
  const currentValueId = currentValue?.id || '';
  const isDesktop = variant === Variant.DESKTOP;
  const isMobile = variant === Variant.MOBILE;
  const availabilityStateInformation: Record<AvailabilityStates, string> = {
    [AvailabilityStates.TemporaryOutOfStock]: t('temporaryOutOfStock'),
    [AvailabilityStates.AvailableSoon]: t('availableSoon'),
    [AvailabilityStates.PreOrder]: t('preOrder'),
    [AvailabilityStates.OnStock]: t('onStock'),
    [AvailabilityStates.OutOfStock]: t('permanentlyOutOfStock'),
    [AvailabilityStates.Historic]: t('permanentlyOutOfStock')
  };

  function handleOnChange(valueId: string) {
    const clickedValue = discriminant.values.find(value => value.id === valueId);
    const clickedProduct = clickedValue?.products.find(
      product => product?.nullableAvailabilityState !== AvailabilityStates.OutOfStock
    );

    if (!clickedProduct) return;

    let url = clickedProduct.urls?.product;

    if (isLeasePage) {
      url += '?lease=1';
      if (hasHandoverSessionCookie()) {
        renewHandoverSessionCookie();
        url += '&bho=1';
      }
    }

    trackEvent(
      selectOptionTrigger({
        sub_feature: 'drop_down_selector',
        index: discriminant.values.findIndex(value => value.id === valueId) + 1, // 1-based index
        total_indices: discriminant.values.length,
        item_id: clickedProduct.id
      })
    );

    if (isDesktop && url) {
      router.push(url);
    }

    if (isMobile && clickedProduct && onProductClick) {
      onProductClick(clickedProduct);
    }
  }

  return (
    <Box {...(isInternalUser && { 'data-cy': 'collection-dropdown' })}>
      <CollectionDropdown
        onOpen={() => {
          trackEvent(
            openCollectionTrigger({
              sub_feature: 'drop_down_selector',
              index: 1,
              item_list_id: product?.id ?? 0,
              item_list_name: product?.name ?? '',
              number_of_items: discriminant.values.length,
              total_indices: 1
            })
          );
        }}
        onClose={() => {
          trackEvent(
            closeCollectionTrigger({
              sub_feature: 'drop_down_selector'
            })
          );
        }}
        onChange={value => handleOnChange(value)}
        ariaLabel={t('collection.dropdownAriaLabel')}
        initialTitle={currentProductName}
        initialValue={currentValueId}
        variant={isMobile ? 'contentOnly' : 'default'}
        dropdownSize={{ sm: 'default', xl: 'extended' }}
      >
        {discriminant.values.map((value, index) => {
          const { productImageId, availabilityState } = getCollectionProductInfo(
            value,
            productsWithCurrentColor
          );

          if (!value.shouldShowValue) {
            return <Fragment key={value.id + index} />;
          }

          const additionalInformation =
            availabilityState === AvailabilityStates.TemporaryOutOfStock ||
            availabilityState === AvailabilityStates.AvailableSoon ||
            availabilityState === AvailabilityStates.PreOrder
              ? availabilityStateInformation[availabilityState]
              : '';

          const priceDifference = shouldShowPriceDifference
            ? getPriceDifference(currentValue, value)
            : '';

          const priceInformation =
            priceDifference && priceDifference > 0
              ? t('collection.priceDifference', {
                  priceDifference: getLanguageSpecificPrice(priceDifference, language)
                })
              : '';

          return (
            <CollectionDropdownOption
              key={value.id + index}
              value={value.id}
              title={value.name || ''}
              description={value.description || ''}
              imageId={productImageId}
              recommended={value.isRecommended}
              priceInformation={priceInformation}
              additionalInformation={additionalInformation}
              isSoldOut={
                availabilityState === AvailabilityStates.TemporaryOutOfStock ||
                availabilityState === AvailabilityStates.AvailableSoon
              }
            />
          );
        })}
      </CollectionDropdown>
    </Box>
  );
};
